import { gql } from '@apollo/client';
import * as Apollo from '@apollo/client';
const defaultOptions = {};
/** The presumed access level within the application. */
export var AccessLevel;
(function (AccessLevel) {
    /** A system admin capable of managing all or part of the system. */
    AccessLevel["Admin"] = "ADMIN";
    /** A standard user participating in the system. */
    AccessLevel["User"] = "USER";
})(AccessLevel || (AccessLevel = {}));
/** A preferred way to meet or engage with other users, events, or activities. */
export var ConnectionPreference;
(function (ConnectionPreference) {
    /** Designates a user is flexible and willing to meet in person or remote conference. */
    ConnectionPreference["Both"] = "BOTH";
    /** Designates a user is hoping to meet in person. */
    ConnectionPreference["Inperson"] = "INPERSON";
    /** Designates a user only wants to meet via videoconference and not in person. */
    ConnectionPreference["Videoconference"] = "VIDEOCONFERENCE";
})(ConnectionPreference || (ConnectionPreference = {}));
/** The current status of an invitation for an event. */
export var EventInvitationStatus;
(function (EventInvitationStatus) {
    /** The invited user has accepted the invitation. */
    EventInvitationStatus["Accepted"] = "ACCEPTED";
    /** The invitation is no longer applicable either due to failure to respond or event cancellation. */
    EventInvitationStatus["Cancelled"] = "CANCELLED";
    /** The invited user has declined the invitation. */
    EventInvitationStatus["Declined"] = "DECLINED";
    /** A finished event that has been reviewed by the invitee. */
    EventInvitationStatus["Finished"] = "FINISHED";
    /** An finished event that needs to be rated by the participant. */
    EventInvitationStatus["NeedsReview"] = "NEEDS_REVIEW";
    /** The invitation belongs to the organizer of the event. */
    EventInvitationStatus["Organizer"] = "ORGANIZER";
    /** The invited user has yet to respond. */
    EventInvitationStatus["Pending"] = "PENDING";
})(EventInvitationStatus || (EventInvitationStatus = {}));
/** The current status of a given event in the system. */
export var EventStatus;
(function (EventStatus) {
    /** An event that has been cancelled by the creator or an admin. */
    EventStatus["Cancelled"] = "CANCELLED";
    /** An event that has been deleted by an admin. */
    EventStatus["Deleted"] = "DELETED";
    /** This is a draft that has been denied by an admin. */
    EventStatus["Denied"] = "DENIED";
    /** A draft can only be viewed by the organizer and is not public. */
    EventStatus["Draft"] = "DRAFT";
    /** An event that was never published and has ended. */
    EventStatus["Expired"] = "EXPIRED";
    /** A published event that has finished. */
    EventStatus["Finished"] = "FINISHED";
    /** This is a draft that has been submitted for review and will be published upon approval. */
    EventStatus["NeedsApproval"] = "NEEDS_APPROVAL";
    /** A published event is viewable to any user who can access events. */
    EventStatus["Published"] = "PUBLISHED";
})(EventStatus || (EventStatus = {}));
/** The method to approach updating the search indicies. */
export var IndexMethod;
(function (IndexMethod) {
    /**
     * This is the default behavior. Simply uploads all existing content
     * to algolia and overrides existing data
     */
    IndexMethod["Append"] = "APPEND";
    /** Delete all items from the algolia index before updating. */
    IndexMethod["Refresh"] = "REFRESH";
    /** Skip any algolia realted indexing. */
    IndexMethod["Skip"] = "SKIP";
})(IndexMethod || (IndexMethod = {}));
/** Used to filter messages when querying. */
export var MessageStatus;
(function (MessageStatus) {
    /** Indicates the message has been read by the current user. */
    MessageStatus["Read"] = "READ";
    /** Indicates the message has been sent by the current user. */
    MessageStatus["Sent"] = "SENT";
    /** Indicates the message has not been read by the current user. */
    MessageStatus["Unread"] = "UNREAD";
})(MessageStatus || (MessageStatus = {}));
/** Define the type of job to perform on an associated data set. */
export var ProcessJobType;
(function (ProcessJobType) {
    /** Import content area data. */
    ProcessJobType["ImportContentAreas"] = "IMPORT_CONTENT_AREAS";
    /** Import job field data. */
    ProcessJobType["ImportJobs"] = "IMPORT_JOBS";
    /** Import school district data. */
    ProcessJobType["ImportSchools"] = "IMPORT_SCHOOLS";
})(ProcessJobType || (ProcessJobType = {}));
/** A filter for which matches to include or exclude from a query. */
export var ProfileMatchFilter;
(function (ProfileMatchFilter) {
    /** Returns all matches. */
    ProfileMatchFilter["All"] = "ALL";
    /** Returns any matches that have been favorited */
    ProfileMatchFilter["Favorites"] = "FAVORITES";
    /** Returns any matches that have been blocked / removed */
    ProfileMatchFilter["Removed"] = "REMOVED";
})(ProfileMatchFilter || (ProfileMatchFilter = {}));
/** Defines a state for a given profile match assigned by the parent user. */
export var ProfileMatchPreference;
(function (ProfileMatchPreference) {
    /** Indicates the match should no longer show up in the user's search results. */
    ProfileMatchPreference["Blocked"] = "BLOCKED";
    /** Indicates the match should be in the user's favorites. */
    ProfileMatchPreference["Favorite"] = "FAVORITE";
    /** Indicates no special treatment should be applied to this match. */
    ProfileMatchPreference["None"] = "NONE";
})(ProfileMatchPreference || (ProfileMatchPreference = {}));
/** A sorting order used to determine in what order a match should be returned. */
export var ProfileMatchPriority;
(function (ProfileMatchPriority) {
    /** Orders all matches by the date they were created on. */
    ProfileMatchPriority["Newest"] = "NEWEST";
    /** Orders all profile matches by score in descending order. */
    ProfileMatchPriority["Relevant"] = "RELEVANT";
})(ProfileMatchPriority || (ProfileMatchPriority = {}));
export var ReportIndexType;
(function (ReportIndexType) {
    /** Indicates the report queries all events. */
    ReportIndexType["Events"] = "EVENTS";
    /** Indicates the report queries all invitations. */
    ReportIndexType["Invitations"] = "INVITATIONS";
    /** Indicates the report queries all profiles. */
    ReportIndexType["Profiles"] = "PROFILES";
})(ReportIndexType || (ReportIndexType = {}));
/** The current status of a profile in the match making system. */
export var ResourceDocumentStatus;
(function (ResourceDocumentStatus) {
    /** A hidden document currently in progress or temporarily hidden. */
    ResourceDocumentStatus["Draft"] = "DRAFT";
    /** An active document viewable to the public. */
    ResourceDocumentStatus["Published"] = "PUBLISHED";
})(ResourceDocumentStatus || (ResourceDocumentStatus = {}));
export var ResourceType;
(function (ResourceType) {
    ResourceType["Counties"] = "COUNTIES";
    ResourceType["Districts"] = "DISTRICTS";
    ResourceType["Schools"] = "SCHOOLS";
})(ResourceType || (ResourceType = {}));
/** The role held by a user in the match making system. */
export var Role;
(function (Role) {
    /** An educator looking to reach out to STEM professionals. */
    Role["Educator"] = "EDUCATOR";
    /** A local business or industry professional */
    Role["Professional"] = "PROFESSIONAL";
})(Role || (Role = {}));
export var SearchIndexIdentifier;
(function (SearchIndexIdentifier) {
    /** All searchable content areas. */
    SearchIndexIdentifier["ContentAreas"] = "CONTENT_AREAS";
    /** All searchable events. */
    SearchIndexIdentifier["Events"] = "EVENTS";
    /** All searchable job fields. */
    SearchIndexIdentifier["JobFields"] = "JOB_FIELDS";
    /** All searchable profiles. */
    SearchIndexIdentifier["Profiles"] = "PROFILES";
    /** All searchable schools. */
    SearchIndexIdentifier["Schools"] = "SCHOOLS";
})(SearchIndexIdentifier || (SearchIndexIdentifier = {}));
/** The current status of a profile in the match making system. */
export var Status;
(function (Status) {
    /** An active profile is one which has been approved and available to the public user base. */
    Status["Active"] = "ACTIVE";
    /** Flagged profiles have been flagged for review by an admin. */
    Status["AdminFlagged"] = "ADMIN_FLAGGED";
    /** These profiles are invisible to the public per a user's preference. */
    Status["Deactivated"] = "DEACTIVATED";
    /** Denied profiles are no longer available to the public user base. */
    Status["Denied"] = "DENIED";
    /**
     * These profiles are awaiting review from a moderator before the user can complete them and not
     * viewable by the public user base.
     */
    Status["NeedsApproval"] = "NEEDS_APPROVAL";
    /** Pending profiles are incomplete and not viewable by the public user base. */
    Status["PendingCompletion"] = "PENDING_COMPLETION";
    /** Reported profiles have received a complaint by another user. */
    Status["Reported"] = "REPORTED";
    /** These profiles are temporarily invisible to the public due to an admin disabling the account. */
    Status["Suspended"] = "SUSPENDED";
})(Status || (Status = {}));
/** The destination bucket for an upload. */
export var UploadBucket;
(function (UploadBucket) {
    /** Indicates the file should be uploaded to the private attachments bucket. */
    UploadBucket["Attachments"] = "ATTACHMENTS";
    /** Indicates the file should be uploaded to the public images bucket. */
    UploadBucket["Images"] = "IMAGES";
    /** Indicates the file should be uploaded to the private pending bucket for additional processing. */
    UploadBucket["Pending"] = "PENDING";
})(UploadBucket || (UploadBucket = {}));
export const AdminContentAreaSearchDocument = gql `
    query AdminContentAreaSearch($queryString: String!, $facetFilters: [String], $page: Int, $pageSize: Int) {
  search(queryString: $queryString) {
    contentAreas(page: $page, pageSize: $pageSize, facetFilters: $facetFilters) {
      nodes {
        id
        standard
        coreIdea
        lowerLevel
      }
      searchInfo {
        page
        pageCount
        facets
      }
    }
    success
  }
  updatingIndicies {
    indexId
    progress
  }
}
    `;
/**
 * __useAdminContentAreaSearchQuery__
 *
 * To run a query within a React component, call `useAdminContentAreaSearchQuery` and pass it any options that fit your needs.
 * When your component renders, `useAdminContentAreaSearchQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAdminContentAreaSearchQuery({
 *   variables: {
 *      queryString: // value for 'queryString'
 *      facetFilters: // value for 'facetFilters'
 *      page: // value for 'page'
 *      pageSize: // value for 'pageSize'
 *   },
 * });
 */
export function useAdminContentAreaSearchQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(AdminContentAreaSearchDocument, options);
}
export function useAdminContentAreaSearchLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(AdminContentAreaSearchDocument, options);
}
export function useAdminContentAreaSearchSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(AdminContentAreaSearchDocument, options);
}
export const AdminJobFieldSearchDocument = gql `
    query AdminJobFieldSearch($queryString: String!, $facetFilters: [String], $page: Int, $pageSize: Int) {
  search(queryString: $queryString) {
    jobFields(page: $page, pageSize: $pageSize, facetFilters: $facetFilters) {
      nodes {
        id
        standard
        field
        lowerLevel
      }
      searchInfo {
        page
        pageCount
        facets
      }
    }
    success
  }
  updatingIndicies {
    indexId
    progress
  }
}
    `;
/**
 * __useAdminJobFieldSearchQuery__
 *
 * To run a query within a React component, call `useAdminJobFieldSearchQuery` and pass it any options that fit your needs.
 * When your component renders, `useAdminJobFieldSearchQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAdminJobFieldSearchQuery({
 *   variables: {
 *      queryString: // value for 'queryString'
 *      facetFilters: // value for 'facetFilters'
 *      page: // value for 'page'
 *      pageSize: // value for 'pageSize'
 *   },
 * });
 */
export function useAdminJobFieldSearchQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(AdminJobFieldSearchDocument, options);
}
export function useAdminJobFieldSearchLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(AdminJobFieldSearchDocument, options);
}
export function useAdminJobFieldSearchSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(AdminJobFieldSearchDocument, options);
}
export const ProcessUploadCsvDocument = gql `
    mutation ProcessUploadCSV($fileKey: String!, $mappings: [FieldMapping]!, $jobType: ProcessJobType!, $includeHeaderRow: Boolean, $wipeExistingData: Boolean) {
  response: processUploadCSV(
    fileKey: $fileKey
    mappings: $mappings
    jobType: $jobType
    includeHeaderRow: $includeHeaderRow
    wipeExistingData: $wipeExistingData
  ) {
    jobId
    fileKey
    errors {
      path
      message
    }
    success
  }
}
    `;
/**
 * __useProcessUploadCsvMutation__
 *
 * To run a mutation, you first call `useProcessUploadCsvMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useProcessUploadCsvMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [processUploadCsvMutation, { data, loading, error }] = useProcessUploadCsvMutation({
 *   variables: {
 *      fileKey: // value for 'fileKey'
 *      mappings: // value for 'mappings'
 *      jobType: // value for 'jobType'
 *      includeHeaderRow: // value for 'includeHeaderRow'
 *      wipeExistingData: // value for 'wipeExistingData'
 *   },
 * });
 */
export function useProcessUploadCsvMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ProcessUploadCsvDocument, options);
}
export const AdminSchoolSearchDocument = gql `
    query AdminSchoolSearch($queryString: String!, $facetFilters: [String], $page: Int, $pageSize: Int) {
  search(queryString: $queryString) {
    schools(page: $page, pageSize: $pageSize, facetFilters: $facetFilters) {
      nodes {
        id
        name
        district {
          id
          name
        }
        city {
          id
          name
        }
      }
      searchInfo {
        page
        pageCount
        facets
      }
    }
    success
  }
  updatingIndicies {
    indexId
    progress
  }
}
    `;
/**
 * __useAdminSchoolSearchQuery__
 *
 * To run a query within a React component, call `useAdminSchoolSearchQuery` and pass it any options that fit your needs.
 * When your component renders, `useAdminSchoolSearchQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAdminSchoolSearchQuery({
 *   variables: {
 *      queryString: // value for 'queryString'
 *      facetFilters: // value for 'facetFilters'
 *      page: // value for 'page'
 *      pageSize: // value for 'pageSize'
 *   },
 * });
 */
export function useAdminSchoolSearchQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(AdminSchoolSearchDocument, options);
}
export function useAdminSchoolSearchLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(AdminSchoolSearchDocument, options);
}
export function useAdminSchoolSearchSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(AdminSchoolSearchDocument, options);
}
export const CitiesDocument = gql `
    query Cities {
  response: cities {
    cities {
      id
      name
    }
    success
  }
}
    `;
/**
 * __useCitiesQuery__
 *
 * To run a query within a React component, call `useCitiesQuery` and pass it any options that fit your needs.
 * When your component renders, `useCitiesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useCitiesQuery({
 *   variables: {
 *   },
 * });
 */
export function useCitiesQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(CitiesDocument, options);
}
export function useCitiesLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(CitiesDocument, options);
}
export function useCitiesSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(CitiesDocument, options);
}
export const ClearContentAreasDocument = gql `
    mutation ClearContentAreas {
  response: clearContentAreas {
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useClearContentAreasMutation__
 *
 * To run a mutation, you first call `useClearContentAreasMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useClearContentAreasMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [clearContentAreasMutation, { data, loading, error }] = useClearContentAreasMutation({
 *   variables: {
 *   },
 * });
 */
export function useClearContentAreasMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ClearContentAreasDocument, options);
}
export const ClearJobFieldsDocument = gql `
    mutation ClearJobFields {
  response: clearJobFields {
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useClearJobFieldsMutation__
 *
 * To run a mutation, you first call `useClearJobFieldsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useClearJobFieldsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [clearJobFieldsMutation, { data, loading, error }] = useClearJobFieldsMutation({
 *   variables: {
 *   },
 * });
 */
export function useClearJobFieldsMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ClearJobFieldsDocument, options);
}
export const ClearSchoolsDocument = gql `
    mutation ClearSchools {
  response: clearSchools {
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useClearSchoolsMutation__
 *
 * To run a mutation, you first call `useClearSchoolsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useClearSchoolsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [clearSchoolsMutation, { data, loading, error }] = useClearSchoolsMutation({
 *   variables: {
 *   },
 * });
 */
export function useClearSchoolsMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ClearSchoolsDocument, options);
}
export const CoreIdeasDocument = gql `
    query CoreIdeas($coreIdea: String) {
  response: contentAreas(coreIdea: $coreIdea) {
    coreIdeas {
      id
      coreIdea
      standard
    }
    success
  }
}
    `;
/**
 * __useCoreIdeasQuery__
 *
 * To run a query within a React component, call `useCoreIdeasQuery` and pass it any options that fit your needs.
 * When your component renders, `useCoreIdeasQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useCoreIdeasQuery({
 *   variables: {
 *      coreIdea: // value for 'coreIdea'
 *   },
 * });
 */
export function useCoreIdeasQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(CoreIdeasDocument, options);
}
export function useCoreIdeasLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(CoreIdeasDocument, options);
}
export function useCoreIdeasSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(CoreIdeasDocument, options);
}
export const DeleteContentAreasDocument = gql `
    mutation DeleteContentAreas($ids: [String]) {
  response: deleteContentAreas(ids: $ids) {
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useDeleteContentAreasMutation__
 *
 * To run a mutation, you first call `useDeleteContentAreasMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteContentAreasMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteContentAreasMutation, { data, loading, error }] = useDeleteContentAreasMutation({
 *   variables: {
 *      ids: // value for 'ids'
 *   },
 * });
 */
export function useDeleteContentAreasMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(DeleteContentAreasDocument, options);
}
export const DeleteJobFieldsDocument = gql `
    mutation DeleteJobFields($ids: [String]) {
  response: deleteJobFields(ids: $ids) {
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useDeleteJobFieldsMutation__
 *
 * To run a mutation, you first call `useDeleteJobFieldsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteJobFieldsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteJobFieldsMutation, { data, loading, error }] = useDeleteJobFieldsMutation({
 *   variables: {
 *      ids: // value for 'ids'
 *   },
 * });
 */
export function useDeleteJobFieldsMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(DeleteJobFieldsDocument, options);
}
export const DeleteSchoolsDocument = gql `
    mutation DeleteSchools($ids: [String]) {
  response: deleteSchools(ids: $ids) {
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useDeleteSchoolsMutation__
 *
 * To run a mutation, you first call `useDeleteSchoolsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteSchoolsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteSchoolsMutation, { data, loading, error }] = useDeleteSchoolsMutation({
 *   variables: {
 *      ids: // value for 'ids'
 *   },
 * });
 */
export function useDeleteSchoolsMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(DeleteSchoolsDocument, options);
}
export const DistrictsDocument = gql `
    query Districts {
  response: districts {
    districts {
      id
      name
      city {
        id
        name
      }
    }
    success
  }
}
    `;
/**
 * __useDistrictsQuery__
 *
 * To run a query within a React component, call `useDistrictsQuery` and pass it any options that fit your needs.
 * When your component renders, `useDistrictsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useDistrictsQuery({
 *   variables: {
 *   },
 * });
 */
export function useDistrictsQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(DistrictsDocument, options);
}
export function useDistrictsLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(DistrictsDocument, options);
}
export function useDistrictsSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(DistrictsDocument, options);
}
export const ImportContentAreasDocument = gql `
    mutation ImportContentAreas($input: [ContentAreaInput]!, $reindex: IndexMethod) {
  response: importContentAreas(input: $input, reindex: $reindex) {
    contentAreas {
      id
      standard
      coreIdea
      lowerLevel
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useImportContentAreasMutation__
 *
 * To run a mutation, you first call `useImportContentAreasMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useImportContentAreasMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [importContentAreasMutation, { data, loading, error }] = useImportContentAreasMutation({
 *   variables: {
 *      input: // value for 'input'
 *      reindex: // value for 'reindex'
 *   },
 * });
 */
export function useImportContentAreasMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ImportContentAreasDocument, options);
}
export const ImportJobFieldsDocument = gql `
    mutation ImportJobFields($input: [JobFieldInput]!, $reindex: IndexMethod) {
  response: importJobFields(input: $input, reindex: $reindex) {
    jobFields {
      id
      standard
      field
      lowerLevel
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useImportJobFieldsMutation__
 *
 * To run a mutation, you first call `useImportJobFieldsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useImportJobFieldsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [importJobFieldsMutation, { data, loading, error }] = useImportJobFieldsMutation({
 *   variables: {
 *      input: // value for 'input'
 *      reindex: // value for 'reindex'
 *   },
 * });
 */
export function useImportJobFieldsMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ImportJobFieldsDocument, options);
}
export const ImportSchoolsDocument = gql `
    mutation ImportSchools($input: [SchoolInput]!, $reindex: IndexMethod) {
  response: importSchools(input: $input, reindex: $reindex) {
    schools {
      id
      district {
        id
        name
      }
      name
      city {
        id
        name
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useImportSchoolsMutation__
 *
 * To run a mutation, you first call `useImportSchoolsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useImportSchoolsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [importSchoolsMutation, { data, loading, error }] = useImportSchoolsMutation({
 *   variables: {
 *      input: // value for 'input'
 *      reindex: // value for 'reindex'
 *   },
 * });
 */
export function useImportSchoolsMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ImportSchoolsDocument, options);
}
export const MakeContentAreaDocument = gql `
    mutation MakeContentArea($input: ContentAreaInput!) {
  response: makeContentArea(input: $input) {
    contentArea {
      id
      standard
      coreIdea
      lowerLevel
    }
    coreIdea
    lowerLevel
    standard
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useMakeContentAreaMutation__
 *
 * To run a mutation, you first call `useMakeContentAreaMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useMakeContentAreaMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [makeContentAreaMutation, { data, loading, error }] = useMakeContentAreaMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
export function useMakeContentAreaMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(MakeContentAreaDocument, options);
}
export const MakeJobFieldDocument = gql `
    mutation MakeJobField($input: JobFieldInput!) {
  response: makeJobField(input: $input) {
    jobField {
      id
      standard
      field
      lowerLevel
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useMakeJobFieldMutation__
 *
 * To run a mutation, you first call `useMakeJobFieldMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useMakeJobFieldMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [makeJobFieldMutation, { data, loading, error }] = useMakeJobFieldMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
export function useMakeJobFieldMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(MakeJobFieldDocument, options);
}
export const MakeSchoolDocument = gql `
    mutation MakeSchool($input: SchoolInput!) {
  response: makeSchool(input: $input) {
    school {
      id
      name
      district {
        id
        name
      }
      city {
        id
        name
      }
    }
    district {
      id
      name
      city {
        id
        name
      }
    }
    city {
      id
      name
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useMakeSchoolMutation__
 *
 * To run a mutation, you first call `useMakeSchoolMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useMakeSchoolMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [makeSchoolMutation, { data, loading, error }] = useMakeSchoolMutation({
 *   variables: {
 *      input: // value for 'input'
 *   },
 * });
 */
export function useMakeSchoolMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(MakeSchoolDocument, options);
}
export const StandardsDocument = gql `
    query Standards($standard: String) {
  response: contentAreas(standard: $standard) {
    standards
    success
  }
}
    `;
/**
 * __useStandardsQuery__
 *
 * To run a query within a React component, call `useStandardsQuery` and pass it any options that fit your needs.
 * When your component renders, `useStandardsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useStandardsQuery({
 *   variables: {
 *      standard: // value for 'standard'
 *   },
 * });
 */
export function useStandardsQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(StandardsDocument, options);
}
export function useStandardsLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(StandardsDocument, options);
}
export function useStandardsSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(StandardsDocument, options);
}
export const AdminEventSearchDocument = gql `
    query AdminEventSearch($queryString: String!, $facetFilters: [String], $page: Int, $pageSize: Int) {
  search(queryString: $queryString) {
    events(page: $page, pageSize: $pageSize, facetFilters: $facetFilters) {
      nodes {
        color
        id
        iconUrl
        name
        description
        averageRatingScore
        totalRatingsScore
        totalRatingsCount
        dates {
          id
          startDate
          endDate
        }
        location {
          id
          city
        }
        organizer {
          id
          firstName
          lastName
        }
      }
      searchInfo {
        page
        pageCount
        facets
      }
    }
    success
  }
}
    `;
/**
 * __useAdminEventSearchQuery__
 *
 * To run a query within a React component, call `useAdminEventSearchQuery` and pass it any options that fit your needs.
 * When your component renders, `useAdminEventSearchQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAdminEventSearchQuery({
 *   variables: {
 *      queryString: // value for 'queryString'
 *      facetFilters: // value for 'facetFilters'
 *      page: // value for 'page'
 *      pageSize: // value for 'pageSize'
 *   },
 * });
 */
export function useAdminEventSearchQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(AdminEventSearchDocument, options);
}
export function useAdminEventSearchLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(AdminEventSearchDocument, options);
}
export function useAdminEventSearchSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(AdminEventSearchDocument, options);
}
export const AdminInvitationsSearchDocument = gql `
    query AdminInvitationsSearch($queryString: String!, $facetFilters: [String], $page: Int, $pageSize: Int) {
  search(queryString: $queryString) {
    invitations(page: $page, pageSize: $pageSize, facetFilters: $facetFilters) {
      nodes {
        id
        eventColor
        eventName
        eventId
        profileColor
        profileName
        profileId
        profileDescription
        profileDescription2
        status
      }
      searchInfo {
        page
        pageCount
        facets
      }
    }
    success
  }
}
    `;
/**
 * __useAdminInvitationsSearchQuery__
 *
 * To run a query within a React component, call `useAdminInvitationsSearchQuery` and pass it any options that fit your needs.
 * When your component renders, `useAdminInvitationsSearchQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAdminInvitationsSearchQuery({
 *   variables: {
 *      queryString: // value for 'queryString'
 *      facetFilters: // value for 'facetFilters'
 *      page: // value for 'page'
 *      pageSize: // value for 'pageSize'
 *   },
 * });
 */
export function useAdminInvitationsSearchQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(AdminInvitationsSearchDocument, options);
}
export function useAdminInvitationsSearchLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(AdminInvitationsSearchDocument, options);
}
export function useAdminInvitationsSearchSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(AdminInvitationsSearchDocument, options);
}
export const DenyEventDocument = gql `
    mutation DenyEvent($eventId: ID!, $reason: String) {
  response: denyEvent(eventId: $eventId, reason: $reason) {
    event {
      id
      publishedAt
      createdAt
      open
      dates {
        id
        startDate
        endDate
      }
      name
      description
      flexibleDates
      flexibleLocation
      location {
        id
        name
        address1
        address1
        city
        zip
      }
      contentAreas
      engagementOpportunities
      gradeLevels
      connectionPreference
      status
      maximumAttendees
      inviteAllProfessionals
      inviteAllEducators
      color
      iconUrl
      iconKey
      invitations {
        id
        profileName
        profileColor
        profileIconUrl
        createdAt
      }
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useDenyEventMutation__
 *
 * To run a mutation, you first call `useDenyEventMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDenyEventMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [denyEventMutation, { data, loading, error }] = useDenyEventMutation({
 *   variables: {
 *      eventId: // value for 'eventId'
 *      reason: // value for 'reason'
 *   },
 * });
 */
export function useDenyEventMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(DenyEventDocument, options);
}
export const DestroyEventDocument = gql `
    mutation DestroyEvent($eventId: ID!) {
  response: destroyEvent(eventId: $eventId) {
    event {
      id
      publishedAt
      createdAt
      open
      dates {
        id
        startDate
        endDate
      }
      name
      description
      flexibleDates
      flexibleLocation
      location {
        id
        name
        address1
        address1
        city
        zip
      }
      contentAreas
      engagementOpportunities
      gradeLevels
      connectionPreference
      status
      maximumAttendees
      inviteAllProfessionals
      inviteAllEducators
      color
      iconUrl
      iconKey
      invitations {
        id
        profileName
        profileColor
        profileIconUrl
        createdAt
      }
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useDestroyEventMutation__
 *
 * To run a mutation, you first call `useDestroyEventMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDestroyEventMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [destroyEventMutation, { data, loading, error }] = useDestroyEventMutation({
 *   variables: {
 *      eventId: // value for 'eventId'
 *   },
 * });
 */
export function useDestroyEventMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(DestroyEventDocument, options);
}
export const FindEventDocument = gql `
    query FindEvent($eventId: String!) {
  response: event(eventId: $eventId) {
    event {
      id
      attachments {
        id
        name
        downloadUrl
        position
      }
      publishedAt
      createdAt
      open
      dates {
        id
        startDate
        endDate
      }
      name
      description
      flexibleDates
      flexibleLocation
      location {
        id
        name
        address1
        address1
        city
        zip
      }
      contentAreas
      contentAreaJobFields
      engagementOpportunities
      gradeLevels
      connectionPreference
      status
      maximumAttendees
      inviteAllProfessionals
      inviteAllEducators
      color
      iconUrl
      iconKey
      organizedBy
      organizer {
        id
        firstName
        lastName
        avatarUrl
        color
      }
      ratings {
        id
        score
        comments
        profileId
        profileColor
        profileName
        profileAvatarUrl
        createdAt
      }
      acceptedInvitations {
        id
        profileName
        profileColor
        profileDescription
        profileDescription2
        profileIconUrl
        profileId
        status
        createdAt
      }
      pendingInvitations {
        id
        profileName
        profileColor
        profileDescription
        profileDescription2
        profileIconUrl
        profileId
        status
        createdAt
      }
      cancelledInvitations {
        id
        profileName
        profileColor
        profileDescription
        profileDescription2
        profileIconUrl
        profileId
        status
        createdAt
      }
      finishedInvitations {
        id
        profileName
        profileColor
        profileDescription
        profileDescription2
        profileIconUrl
        profileId
        status
        createdAt
      }
      reviewableInvitations {
        id
        profileName
        profileColor
        profileDescription
        profileDescription2
        profileIconUrl
        profileId
        status
        createdAt
      }
    }
    relatedInvitation {
      id
      eventId
      eventName
      eventColor
      eventIconUrl
      eventDates {
        id
        startDate
        endDate
      }
      eventLocation
      profileColor
      profileDescription
      profileDescription2
      profileId
      profileName
      status
      createdAt
      updatedAt
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useFindEventQuery__
 *
 * To run a query within a React component, call `useFindEventQuery` and pass it any options that fit your needs.
 * When your component renders, `useFindEventQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useFindEventQuery({
 *   variables: {
 *      eventId: // value for 'eventId'
 *   },
 * });
 */
export function useFindEventQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(FindEventDocument, options);
}
export function useFindEventLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(FindEventDocument, options);
}
export function useFindEventSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(FindEventDocument, options);
}
export const ActivateProfileDocument = gql `
    mutation ActivateProfile($id: String!) {
  response: activateProfile(id: $id) {
    profile {
      ... on Profile {
        id
        status
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useActivateProfileMutation__
 *
 * To run a mutation, you first call `useActivateProfileMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useActivateProfileMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [activateProfileMutation, { data, loading, error }] = useActivateProfileMutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useActivateProfileMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ActivateProfileDocument, options);
}
export const AdminProfileSearchDocument = gql `
    query AdminProfileSearch($queryString: String!, $facetFilters: [String], $page: Int, $pageSize: Int) {
  search(queryString: $queryString) {
    profiles(page: $page, pageSize: $pageSize, facetFilters: $facetFilters) {
      nodes {
        id
        avatarUrl
        about
        color
        createdAt
        firstName
        lastName
        role
        ... on Educator {
          schoolName
          districtName
        }
        ... on Professional {
          companyName
          jobField
        }
      }
      searchInfo {
        page
        pageCount
        facets
      }
    }
    success
  }
}
    `;
/**
 * __useAdminProfileSearchQuery__
 *
 * To run a query within a React component, call `useAdminProfileSearchQuery` and pass it any options that fit your needs.
 * When your component renders, `useAdminProfileSearchQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAdminProfileSearchQuery({
 *   variables: {
 *      queryString: // value for 'queryString'
 *      facetFilters: // value for 'facetFilters'
 *      page: // value for 'page'
 *      pageSize: // value for 'pageSize'
 *   },
 * });
 */
export function useAdminProfileSearchQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(AdminProfileSearchDocument, options);
}
export function useAdminProfileSearchLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(AdminProfileSearchDocument, options);
}
export function useAdminProfileSearchSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(AdminProfileSearchDocument, options);
}
export const DeactivateProfileDocument = gql `
    mutation DeactivateProfile($id: String!) {
  response: deactivateProfile(id: $id) {
    profile {
      ... on Profile {
        id
        status
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useDeactivateProfileMutation__
 *
 * To run a mutation, you first call `useDeactivateProfileMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeactivateProfileMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deactivateProfileMutation, { data, loading, error }] = useDeactivateProfileMutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useDeactivateProfileMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(DeactivateProfileDocument, options);
}
export const DestroyProfileDocument = gql `
    mutation DestroyProfile($id: String!) {
  response: destroyProfile(id: $id) {
    profile {
      ... on Profile {
        id
        status
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useDestroyProfileMutation__
 *
 * To run a mutation, you first call `useDestroyProfileMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDestroyProfileMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [destroyProfileMutation, { data, loading, error }] = useDestroyProfileMutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useDestroyProfileMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(DestroyProfileDocument, options);
}
export const EducatorsWithStatusDocument = gql `
    query EducatorsWithStatus($status: Status!, $after: ID, $before: ID, $first: Int, $last: Int) {
  educators(status: $status) {
    connection(
      connection: {first: $first, last: $last, after: $after, before: $before}
    ) {
      nodes {
        ... on Profile {
          about
          avatarUrl
          color
          id
          firstName
          lastName
          role
          status
          createdAt
          approvedBy
          approvedAt
        }
        ... on Educator {
          schoolName
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasPreviousPages(amount: 5) {
          cursor
        }
        hasNextPages(amount: 5) {
          cursor
        }
        hasPreviousPage
        hasNextPage
      }
    }
  }
}
    `;
/**
 * __useEducatorsWithStatusQuery__
 *
 * To run a query within a React component, call `useEducatorsWithStatusQuery` and pass it any options that fit your needs.
 * When your component renders, `useEducatorsWithStatusQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useEducatorsWithStatusQuery({
 *   variables: {
 *      status: // value for 'status'
 *      after: // value for 'after'
 *      before: // value for 'before'
 *      first: // value for 'first'
 *      last: // value for 'last'
 *   },
 * });
 */
export function useEducatorsWithStatusQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(EducatorsWithStatusDocument, options);
}
export function useEducatorsWithStatusLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(EducatorsWithStatusDocument, options);
}
export function useEducatorsWithStatusSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(EducatorsWithStatusDocument, options);
}
export const FlagProfileDocument = gql `
    mutation FlagProfile($id: String!) {
  response: flagProfile(id: $id) {
    profile {
      ... on Profile {
        id
        status
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useFlagProfileMutation__
 *
 * To run a mutation, you first call `useFlagProfileMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useFlagProfileMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [flagProfileMutation, { data, loading, error }] = useFlagProfileMutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useFlagProfileMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(FlagProfileDocument, options);
}
export const ProfessionalsWithStatusDocument = gql `
    query ProfessionalsWithStatus($status: Status!, $after: ID, $before: ID, $first: Int, $last: Int) {
  professionals(status: $status) {
    connection(
      connection: {first: $first, last: $last, after: $after, before: $before}
    ) {
      nodes {
        ... on Profile {
          about
          avatarUrl
          color
          id
          firstName
          lastName
          role
          status
          createdAt
          approvedBy
          approvedAt
        }
        ... on Professional {
          companyName
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasPreviousPages(amount: 5) {
          cursor
        }
        hasNextPages(amount: 5) {
          cursor
        }
        hasPreviousPage
        hasNextPage
      }
    }
  }
}
    `;
/**
 * __useProfessionalsWithStatusQuery__
 *
 * To run a query within a React component, call `useProfessionalsWithStatusQuery` and pass it any options that fit your needs.
 * When your component renders, `useProfessionalsWithStatusQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useProfessionalsWithStatusQuery({
 *   variables: {
 *      status: // value for 'status'
 *      after: // value for 'after'
 *      before: // value for 'before'
 *      first: // value for 'first'
 *      last: // value for 'last'
 *   },
 * });
 */
export function useProfessionalsWithStatusQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(ProfessionalsWithStatusDocument, options);
}
export function useProfessionalsWithStatusLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(ProfessionalsWithStatusDocument, options);
}
export function useProfessionalsWithStatusSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(ProfessionalsWithStatusDocument, options);
}
export const CreateReportDocument = gql `
    mutation CreateReport($payload: ReportPayload!) {
  createReport(payload: $payload) {
    report {
      id
      description
      name
      queryString
      facetFilters
      indexType
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useCreateReportMutation__
 *
 * To run a mutation, you first call `useCreateReportMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateReportMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createReportMutation, { data, loading, error }] = useCreateReportMutation({
 *   variables: {
 *      payload: // value for 'payload'
 *   },
 * });
 */
export function useCreateReportMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(CreateReportDocument, options);
}
export const DestroyReportDocument = gql `
    mutation DestroyReport($id: ID!) {
  destroyReport(id: $id) {
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useDestroyReportMutation__
 *
 * To run a mutation, you first call `useDestroyReportMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDestroyReportMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [destroyReportMutation, { data, loading, error }] = useDestroyReportMutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useDestroyReportMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(DestroyReportDocument, options);
}
export const DownloadReportDocument = gql `
    query DownloadReport($id: ID!) {
  downloadReport(id: $id) {
    downloadUrl
    success
    errors {
      path
      message
    }
  }
}
    `;
/**
 * __useDownloadReportQuery__
 *
 * To run a query within a React component, call `useDownloadReportQuery` and pass it any options that fit your needs.
 * When your component renders, `useDownloadReportQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useDownloadReportQuery({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useDownloadReportQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(DownloadReportDocument, options);
}
export function useDownloadReportLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(DownloadReportDocument, options);
}
export function useDownloadReportSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(DownloadReportDocument, options);
}
export const ReportsDocument = gql `
    query Reports {
  events: reports(indexType: EVENTS) {
    reports {
      id
      description
      name
      queryString
      facetFilters
      indexType
    }
    success
    errors {
      path
      message
    }
  }
  profiles: reports(indexType: PROFILES) {
    reports {
      id
      description
      name
      queryString
      facetFilters
      indexType
    }
    success
    errors {
      path
      message
    }
  }
  invitations: reports(indexType: INVITATIONS) {
    reports {
      id
      description
      name
      queryString
      facetFilters
      indexType
    }
    success
    errors {
      path
      message
    }
  }
}
    `;
/**
 * __useReportsQuery__
 *
 * To run a query within a React component, call `useReportsQuery` and pass it any options that fit your needs.
 * When your component renders, `useReportsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useReportsQuery({
 *   variables: {
 *   },
 * });
 */
export function useReportsQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(ReportsDocument, options);
}
export function useReportsLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(ReportsDocument, options);
}
export function useReportsSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(ReportsDocument, options);
}
export const AdminResourceDocumentSearchDocument = gql `
    query AdminResourceDocumentSearch($queryString: String!, $facetFilters: [String], $page: Int, $pageSize: Int) {
  search(queryString: $queryString) {
    documents(page: $page, pageSize: $pageSize, facetFilters: $facetFilters) {
      nodes {
        id
        slug
        title
        color
        iconUrl
        contentPreview
        categoryId
        categoryName
        categorySlug
        iconUrl
        excerpt
        featured
        category {
          id
          name
          slug
        }
        status
        updatedAt
        publishedAt
      }
      searchInfo {
        page
        pageCount
        facets
      }
    }
    success
  }
}
    `;
/**
 * __useAdminResourceDocumentSearchQuery__
 *
 * To run a query within a React component, call `useAdminResourceDocumentSearchQuery` and pass it any options that fit your needs.
 * When your component renders, `useAdminResourceDocumentSearchQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAdminResourceDocumentSearchQuery({
 *   variables: {
 *      queryString: // value for 'queryString'
 *      facetFilters: // value for 'facetFilters'
 *      page: // value for 'page'
 *      pageSize: // value for 'pageSize'
 *   },
 * });
 */
export function useAdminResourceDocumentSearchQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(AdminResourceDocumentSearchDocument, options);
}
export function useAdminResourceDocumentSearchLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(AdminResourceDocumentSearchDocument, options);
}
export function useAdminResourceDocumentSearchSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(AdminResourceDocumentSearchDocument, options);
}
export const CreateResourceCategoryDocument = gql `
    mutation CreateResourceCategory($payload: ResourceCategoryInput!) {
  response: createResourceCategory(payload: $payload) {
    category {
      id
      slug
      name
      documents {
        id
        title
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useCreateResourceCategoryMutation__
 *
 * To run a mutation, you first call `useCreateResourceCategoryMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateResourceCategoryMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createResourceCategoryMutation, { data, loading, error }] = useCreateResourceCategoryMutation({
 *   variables: {
 *      payload: // value for 'payload'
 *   },
 * });
 */
export function useCreateResourceCategoryMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(CreateResourceCategoryDocument, options);
}
export const CreateResourceDocumentDocument = gql `
    mutation CreateResourceDocument($payload: ResourceDocumentInput!) {
  response: createResourceDocument(payload: $payload) {
    document {
      id
      slug
      title
      color
      iconUrl
      content
      contentPreview
      categoryId
      categoryName
      categorySlug
      iconUrl
      excerpt
      featured
      category {
        id
        name
        slug
      }
      status
      updatedAt
      publishedAt
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useCreateResourceDocumentMutation__
 *
 * To run a mutation, you first call `useCreateResourceDocumentMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateResourceDocumentMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createResourceDocumentMutation, { data, loading, error }] = useCreateResourceDocumentMutation({
 *   variables: {
 *      payload: // value for 'payload'
 *   },
 * });
 */
export function useCreateResourceDocumentMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(CreateResourceDocumentDocument, options);
}
export const DestroyResourceCategoryDocument = gql `
    mutation DestroyResourceCategory($id: String!) {
  response: destroyResourceCategory(id: $id) {
    category {
      id
      slug
      name
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useDestroyResourceCategoryMutation__
 *
 * To run a mutation, you first call `useDestroyResourceCategoryMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDestroyResourceCategoryMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [destroyResourceCategoryMutation, { data, loading, error }] = useDestroyResourceCategoryMutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useDestroyResourceCategoryMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(DestroyResourceCategoryDocument, options);
}
export const DestroyResourceDocumentDocument = gql `
    mutation DestroyResourceDocument($id: String!) {
  response: destroyResourceDocument(id: $id) {
    document {
      id
      slug
      title
      color
      iconUrl
      content
      contentPreview
      categoryId
      categoryName
      categorySlug
      iconUrl
      excerpt
      featured
      category {
        id
        name
        slug
      }
      status
      updatedAt
      publishedAt
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useDestroyResourceDocumentMutation__
 *
 * To run a mutation, you first call `useDestroyResourceDocumentMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDestroyResourceDocumentMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [destroyResourceDocumentMutation, { data, loading, error }] = useDestroyResourceDocumentMutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useDestroyResourceDocumentMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(DestroyResourceDocumentDocument, options);
}
export const FindResourceCategoryDocument = gql `
    query FindResourceCategory($id: String!) {
  response: resourceCategory(id: $id) {
    category {
      id
      slug
      name
      documents {
        id
        title
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useFindResourceCategoryQuery__
 *
 * To run a query within a React component, call `useFindResourceCategoryQuery` and pass it any options that fit your needs.
 * When your component renders, `useFindResourceCategoryQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useFindResourceCategoryQuery({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useFindResourceCategoryQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(FindResourceCategoryDocument, options);
}
export function useFindResourceCategoryLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(FindResourceCategoryDocument, options);
}
export function useFindResourceCategorySuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(FindResourceCategoryDocument, options);
}
export const FindResourceDocumentDocument = gql `
    query FindResourceDocument($slug: String!) {
  response: resourceDocumentWithSlug(slug: $slug) {
    document {
      id
      slug
      title
      color
      iconUrl
      content
      contentPreview
      categoryId
      categoryName
      categorySlug
      excerpt
      featured
      category {
        id
        name
        slug
      }
      iconUrl
      status
      updatedAt
      publishedAt
    }
    errors {
      message
      path
    }
    success
  }
  resourceCategories {
    categories {
      id
      name
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useFindResourceDocumentQuery__
 *
 * To run a query within a React component, call `useFindResourceDocumentQuery` and pass it any options that fit your needs.
 * When your component renders, `useFindResourceDocumentQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useFindResourceDocumentQuery({
 *   variables: {
 *      slug: // value for 'slug'
 *   },
 * });
 */
export function useFindResourceDocumentQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(FindResourceDocumentDocument, options);
}
export function useFindResourceDocumentLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(FindResourceDocumentDocument, options);
}
export function useFindResourceDocumentSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(FindResourceDocumentDocument, options);
}
export const ResourceCategoriesDocument = gql `
    query ResourceCategories {
  resourceCategories {
    categories {
      id
      name
      slug
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useResourceCategoriesQuery__
 *
 * To run a query within a React component, call `useResourceCategoriesQuery` and pass it any options that fit your needs.
 * When your component renders, `useResourceCategoriesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useResourceCategoriesQuery({
 *   variables: {
 *   },
 * });
 */
export function useResourceCategoriesQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(ResourceCategoriesDocument, options);
}
export function useResourceCategoriesLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(ResourceCategoriesDocument, options);
}
export function useResourceCategoriesSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(ResourceCategoriesDocument, options);
}
export const UpdateResourceCategoryDocument = gql `
    mutation UpdateResourceCategory($payload: ResourceCategoryInput!) {
  response: updateResourceCategory(payload: $payload) {
    category {
      id
      slug
      name
      documents {
        id
        title
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useUpdateResourceCategoryMutation__
 *
 * To run a mutation, you first call `useUpdateResourceCategoryMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateResourceCategoryMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateResourceCategoryMutation, { data, loading, error }] = useUpdateResourceCategoryMutation({
 *   variables: {
 *      payload: // value for 'payload'
 *   },
 * });
 */
export function useUpdateResourceCategoryMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(UpdateResourceCategoryDocument, options);
}
export const UpdateResourceDocumentDocument = gql `
    mutation UpdateResourceDocument($payload: ResourceDocumentInput!) {
  response: updateResourceDocument(payload: $payload) {
    document {
      id
      slug
      title
      color
      iconUrl
      content
      contentPreview
      categoryId
      categorySlug
      categoryName
      iconUrl
      excerpt
      featured
      category {
        id
        name
        slug
      }
      status
      updatedAt
      publishedAt
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useUpdateResourceDocumentMutation__
 *
 * To run a mutation, you first call `useUpdateResourceDocumentMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateResourceDocumentMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateResourceDocumentMutation, { data, loading, error }] = useUpdateResourceDocumentMutation({
 *   variables: {
 *      payload: // value for 'payload'
 *   },
 * });
 */
export function useUpdateResourceDocumentMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(UpdateResourceDocumentDocument, options);
}
export const AdminUserDocument = gql `
    query AdminUser($id: String!) {
  response: user(id: $id) {
    user {
      id
      emailAddress
      firstName
      lastName
      accessLevel
      profileId
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useAdminUserQuery__
 *
 * To run a query within a React component, call `useAdminUserQuery` and pass it any options that fit your needs.
 * When your component renders, `useAdminUserQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAdminUserQuery({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useAdminUserQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(AdminUserDocument, options);
}
export function useAdminUserLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(AdminUserDocument, options);
}
export function useAdminUserSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(AdminUserDocument, options);
}
export const AdminUsersDocument = gql `
    query AdminUsers($accessLevel: AccessLevel, $after: ID, $before: ID, $first: Int, $last: Int) {
  response: users(accessLevel: $accessLevel) {
    connection(
      connection: {first: $first, last: $last, after: $after, before: $before}
    ) {
      nodes {
        id
        emailAddress
        firstName
        lastName
        accessLevel
        profileId
      }
      pageInfo {
        endCursor
        startCursor
        hasPreviousPages(amount: 5) {
          cursor
        }
        hasNextPages(amount: 5) {
          cursor
        }
        hasPreviousPage
        hasNextPage
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useAdminUsersQuery__
 *
 * To run a query within a React component, call `useAdminUsersQuery` and pass it any options that fit your needs.
 * When your component renders, `useAdminUsersQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAdminUsersQuery({
 *   variables: {
 *      accessLevel: // value for 'accessLevel'
 *      after: // value for 'after'
 *      before: // value for 'before'
 *      first: // value for 'first'
 *      last: // value for 'last'
 *   },
 * });
 */
export function useAdminUsersQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(AdminUsersDocument, options);
}
export function useAdminUsersLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(AdminUsersDocument, options);
}
export function useAdminUsersSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(AdminUsersDocument, options);
}
export const CreateAdminUserDocument = gql `
    mutation CreateAdminUser($user: NewUserInput!) {
  response: createConfirmableUser(userInput: $user) {
    user {
      id
      emailAddress
      firstName
      lastName
      accessLevel
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useCreateAdminUserMutation__
 *
 * To run a mutation, you first call `useCreateAdminUserMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateAdminUserMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createAdminUserMutation, { data, loading, error }] = useCreateAdminUserMutation({
 *   variables: {
 *      user: // value for 'user'
 *   },
 * });
 */
export function useCreateAdminUserMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(CreateAdminUserDocument, options);
}
export const UpdateAdminUserDocument = gql `
    mutation UpdateAdminUser($user: ExistingUserInput!) {
  response: updateUser(userInput: $user) {
    user {
      id
      emailAddress
      firstName
      lastName
      accessLevel
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useUpdateAdminUserMutation__
 *
 * To run a mutation, you first call `useUpdateAdminUserMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateAdminUserMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateAdminUserMutation, { data, loading, error }] = useUpdateAdminUserMutation({
 *   variables: {
 *      user: // value for 'user'
 *   },
 * });
 */
export function useUpdateAdminUserMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(UpdateAdminUserDocument, options);
}
export const ApproveProfileDocument = gql `
    mutation ApproveProfile($id: String!) {
  response: approveProfile(id: $id) {
    profile {
      ... on Profile {
        id
        approvedBy
        approvedAt
        status
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useApproveProfileMutation__
 *
 * To run a mutation, you first call `useApproveProfileMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useApproveProfileMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [approveProfileMutation, { data, loading, error }] = useApproveProfileMutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useApproveProfileMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ApproveProfileDocument, options);
}
export const DenyProfileDocument = gql `
    mutation DenyProfile($id: String!, $reason: String!, $message: String!) {
  response: denyProfile(id: $id, reason: $reason, message: $message) {
    profile {
      ... on Profile {
        id
        deniedBy
        deniedAt
        status
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useDenyProfileMutation__
 *
 * To run a mutation, you first call `useDenyProfileMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDenyProfileMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [denyProfileMutation, { data, loading, error }] = useDenyProfileMutation({
 *   variables: {
 *      id: // value for 'id'
 *      reason: // value for 'reason'
 *      message: // value for 'message'
 *   },
 * });
 */
export function useDenyProfileMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(DenyProfileDocument, options);
}
export const ProfileDocument = gql `
    query Profile($id: String!) {
  response: profile(id: $id) {
    profile {
      ... on Profile {
        id
        about
        avatarUrl
        invitationUrl
        color
        bio
        firstName
        lastName
        emailAddress
        phone
        contentAreas
        about
        keywords
        connectionPreference
        gradeLevels
        otherSecondaryLanguages
        secondaryLanguages
        role
        status
        createdAt
        approvedBy
        approvedAt
        matches {
          id
          matchedProfileId
          title
          avatarUrl
          favorite
          name
          color
        }
      }
      ... on Educator {
        districtId
        districtName
        schoolId
        schoolName
      }
      ... on Professional {
        cities
        preferredActivities
        companyName
        contentAreaJobFields
        jobFieldLowerLevels
        jobField
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useProfileQuery__
 *
 * To run a query within a React component, call `useProfileQuery` and pass it any options that fit your needs.
 * When your component renders, `useProfileQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useProfileQuery({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useProfileQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(ProfileDocument, options);
}
export function useProfileLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(ProfileDocument, options);
}
export function useProfileSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(ProfileDocument, options);
}
export const SuspendProfileDocument = gql `
    mutation SuspendProfile($id: String!) {
  response: suspendProfile(id: $id) {
    profile {
      ... on Profile {
        id
        status
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useSuspendProfileMutation__
 *
 * To run a mutation, you first call `useSuspendProfileMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useSuspendProfileMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [suspendProfileMutation, { data, loading, error }] = useSuspendProfileMutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useSuspendProfileMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(SuspendProfileDocument, options);
}
export const AdminSearchDocument = gql `
    query AdminSearch($queryString: String!, $page: Int) {
  search(queryString: $queryString) {
    profiles(page: $page) {
      nodes {
        id
        avatarUrl
        color
        firstName
        lastName
        role
        ... on Educator {
          schoolName
          districtName
        }
        ... on Professional {
          companyName
          jobField
        }
      }
      searchInfo {
        page
        pageCount
      }
    }
    events(page: $page) {
      nodes {
        color
        id
        iconUrl
        name
        description
        dates {
          id
          startDate
          endDate
        }
        location {
          id
          city
        }
      }
      searchInfo {
        page
        pageCount
        facets
      }
    }
    success
  }
}
    `;
/**
 * __useAdminSearchQuery__
 *
 * To run a query within a React component, call `useAdminSearchQuery` and pass it any options that fit your needs.
 * When your component renders, `useAdminSearchQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAdminSearchQuery({
 *   variables: {
 *      queryString: // value for 'queryString'
 *      page: // value for 'page'
 *   },
 * });
 */
export function useAdminSearchQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(AdminSearchDocument, options);
}
export function useAdminSearchLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(AdminSearchDocument, options);
}
export function useAdminSearchSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(AdminSearchDocument, options);
}
export const NavigatorSearchDocument = gql `
    query NavigatorSearch($queryString: String!, $page: Int) {
  search(queryString: $queryString, useDirectory: true) {
    profiles(page: $page) {
      nodes {
        id
        avatarUrl
        color
        firstName
        lastName
        role
        ... on Educator {
          schoolName
          districtName
        }
        ... on Professional {
          companyName
          jobField
        }
      }
      searchInfo {
        page
        pageCount
      }
    }
    events(page: $page) {
      nodes {
        color
        id
        iconUrl
        name
        description
        dates {
          id
          startDate
          endDate
        }
        location {
          id
          city
        }
      }
      searchInfo {
        page
        pageCount
        facets
      }
    }
    success
  }
}
    `;
/**
 * __useNavigatorSearchQuery__
 *
 * To run a query within a React component, call `useNavigatorSearchQuery` and pass it any options that fit your needs.
 * When your component renders, `useNavigatorSearchQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useNavigatorSearchQuery({
 *   variables: {
 *      queryString: // value for 'queryString'
 *      page: // value for 'page'
 *   },
 * });
 */
export function useNavigatorSearchQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(NavigatorSearchDocument, options);
}
export function useNavigatorSearchLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(NavigatorSearchDocument, options);
}
export function useNavigatorSearchSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(NavigatorSearchDocument, options);
}
export const ProfileForNavigationDocument = gql `
    query ProfileForNavigation {
  response: me {
    profile {
      ... on Profile {
        id
        avatarUrl
        color
        firstName
        lastName
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useProfileForNavigationQuery__
 *
 * To run a query within a React component, call `useProfileForNavigationQuery` and pass it any options that fit your needs.
 * When your component renders, `useProfileForNavigationQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useProfileForNavigationQuery({
 *   variables: {
 *   },
 * });
 */
export function useProfileForNavigationQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(ProfileForNavigationDocument, options);
}
export function useProfileForNavigationLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(ProfileForNavigationDocument, options);
}
export function useProfileForNavigationSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(ProfileForNavigationDocument, options);
}
export const DashboardDataDocument = gql `
    query DashboardData {
  response: me {
    profile {
      ... on Profile {
        id
        avatarUrl
        color
        firstName
        lastName
        matches(priority: NEWEST) {
          createdAt
          id
          color
          matchedProfileId
          avatarUrl
          name
          title
          score
        }
        pendingInvitations {
          id
          eventName
          eventColor
          eventIconUrl
          eventId
          eventLocation
          eventDates {
            startDate
            endDate
          }
          status
          createdAt
        }
        acceptedInvitations {
          id
          eventName
          eventColor
          eventIconUrl
          eventId
          eventLocation
          eventDates {
            startDate
            endDate
          }
          status
          createdAt
        }
      }
    }
    errors {
      message
      path
    }
    success
  }
  myEvents {
    connection {
      nodes {
        id
        publishedAt
        createdAt
        open
        dates {
          id
          startDate
          endDate
        }
        name
        description
        flexibleDates
        status
        color
        iconUrl
        iconKey
      }
    }
    success
    errors {
      message
      path
    }
  }
  unreadMessages: messages(status: UNREAD) {
    messages {
      id
      content
      avatarUrl
      color
      name
      profileId
      threadId
      updatedAt
      readByUser
      archived
    }
    success
    errors {
      path
      message
    }
  }
  news: search(queryString: "", published: true) {
    documents(page: 0, pageSize: 2, facetFilters: ["categoryName:news"]) {
      nodes {
        id
        slug
        title
        color
        iconUrl
        contentPreview
        categoryId
        categorySlug
        categoryName
        publishedAt
        iconUrl
        category {
          id
          name
        }
        excerpt
        featured
        status
        updatedAt
      }
      searchInfo {
        page
        pageCount
        facets
      }
    }
    success
  }
}
    `;
/**
 * __useDashboardDataQuery__
 *
 * To run a query within a React component, call `useDashboardDataQuery` and pass it any options that fit your needs.
 * When your component renders, `useDashboardDataQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useDashboardDataQuery({
 *   variables: {
 *   },
 * });
 */
export function useDashboardDataQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(DashboardDataDocument, options);
}
export function useDashboardDataLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(DashboardDataDocument, options);
}
export function useDashboardDataSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(DashboardDataDocument, options);
}
export const DirectorySearchDocument = gql `
    query DirectorySearch($queryString: String!, $facetFilters: [String], $page: Int, $pageSize: Int) {
  search(queryString: $queryString, useDirectory: true) {
    profiles(page: $page, pageSize: $pageSize, facetFilters: $facetFilters) {
      nodes {
        id
        avatarUrl
        about
        color
        createdAt
        firstName
        lastName
        role
        ... on Educator {
          schoolName
          districtName
        }
        ... on Professional {
          companyName
          jobField
        }
      }
      searchInfo {
        page
        pageCount
        facets
      }
    }
    success
  }
}
    `;
/**
 * __useDirectorySearchQuery__
 *
 * To run a query within a React component, call `useDirectorySearchQuery` and pass it any options that fit your needs.
 * When your component renders, `useDirectorySearchQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useDirectorySearchQuery({
 *   variables: {
 *      queryString: // value for 'queryString'
 *      facetFilters: // value for 'facetFilters'
 *      page: // value for 'page'
 *      pageSize: // value for 'pageSize'
 *   },
 * });
 */
export function useDirectorySearchQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(DirectorySearchDocument, options);
}
export function useDirectorySearchLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(DirectorySearchDocument, options);
}
export function useDirectorySearchSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(DirectorySearchDocument, options);
}
export const AcceptEventInvitationDocument = gql `
    mutation AcceptEventInvitation($invitation: EventInvitationInput!) {
  response: acceptInvitation(invitation: $invitation) {
    invitation {
      id
      eventId
      eventName
      eventColor
      eventIconUrl
      eventDates {
        id
        startDate
        endDate
      }
      eventLocation
      profileColor
      profileDescription
      profileDescription2
      profileId
      profileName
      status
      notes
      createdAt
      updatedAt
    }
    success
    errors {
      path
      message
    }
  }
}
    `;
/**
 * __useAcceptEventInvitationMutation__
 *
 * To run a mutation, you first call `useAcceptEventInvitationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useAcceptEventInvitationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [acceptEventInvitationMutation, { data, loading, error }] = useAcceptEventInvitationMutation({
 *   variables: {
 *      invitation: // value for 'invitation'
 *   },
 * });
 */
export function useAcceptEventInvitationMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(AcceptEventInvitationDocument, options);
}
export const AddNotesToEventDocument = gql `
    mutation AddNotesToEvent($eventId: ID!, $notes: String!) {
  response: addNotesToEvent(eventId: $eventId, notes: $notes) {
    invitation {
      id
      eventId
      eventName
      eventColor
      eventIconUrl
      eventDates {
        id
        startDate
        endDate
      }
      eventLocation
      profileColor
      profileDescription
      profileDescription2
      profileId
      profileName
      status
      createdAt
      updatedAt
      notes
    }
    success
    errors {
      path
      message
    }
  }
}
    `;
/**
 * __useAddNotesToEventMutation__
 *
 * To run a mutation, you first call `useAddNotesToEventMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useAddNotesToEventMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [addNotesToEventMutation, { data, loading, error }] = useAddNotesToEventMutation({
 *   variables: {
 *      eventId: // value for 'eventId'
 *      notes: // value for 'notes'
 *   },
 * });
 */
export function useAddNotesToEventMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(AddNotesToEventDocument, options);
}
export const ArchiveEventDocument = gql `
    mutation ArchiveEvent($eventId: ID!) {
  response: archiveEvent(eventId: $eventId) {
    event {
      id
      publishedAt
      createdAt
      archived
      open
      dates {
        id
        startDate
        endDate
      }
      name
      description
      flexibleDates
      flexibleTimes
      flexibleLocation
      location {
        id
        name
        address1
        address1
        city
        zip
      }
      contentAreas
      engagementOpportunities
      gradeLevels
      connectionPreference
      status
      maximumAttendees
      inviteAllProfessionals
      inviteAllEducators
      color
      iconUrl
      iconKey
      invitations {
        id
        profileName
        profileColor
        profileIconUrl
        createdAt
      }
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useArchiveEventMutation__
 *
 * To run a mutation, you first call `useArchiveEventMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useArchiveEventMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [archiveEventMutation, { data, loading, error }] = useArchiveEventMutation({
 *   variables: {
 *      eventId: // value for 'eventId'
 *   },
 * });
 */
export function useArchiveEventMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ArchiveEventDocument, options);
}
export const CancelEventDocument = gql `
    mutation CancelEvent($eventId: ID!, $reason: String) {
  response: cancelEvent(eventId: $eventId, reason: $reason) {
    event {
      id
      archived
      publishedAt
      createdAt
      open
      dates {
        id
        startDate
        endDate
      }
      name
      description
      flexibleDates
      flexibleTimes
      flexibleLocation
      location {
        id
        name
        address1
        address1
        city
        zip
      }
      contentAreas
      engagementOpportunities
      gradeLevels
      connectionPreference
      status
      maximumAttendees
      inviteAllProfessionals
      inviteAllEducators
      color
      iconUrl
      iconKey
      invitations {
        id
        profileName
        profileColor
        profileIconUrl
        createdAt
      }
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useCancelEventMutation__
 *
 * To run a mutation, you first call `useCancelEventMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCancelEventMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [cancelEventMutation, { data, loading, error }] = useCancelEventMutation({
 *   variables: {
 *      eventId: // value for 'eventId'
 *      reason: // value for 'reason'
 *   },
 * });
 */
export function useCancelEventMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(CancelEventDocument, options);
}
export const CancelEventInvitationDocument = gql `
    mutation CancelEventInvitation($invitation: EventInvitationInput!) {
  response: cancelInvitation(invitation: $invitation) {
    invitation {
      id
      eventId
      eventName
      eventColor
      eventIconUrl
      eventDates {
        id
        startDate
        endDate
      }
      eventLocation
      profileColor
      profileDescription
      profileDescription2
      profileId
      profileName
      notes
      status
      createdAt
      updatedAt
    }
    success
    errors {
      path
      message
    }
  }
}
    `;
/**
 * __useCancelEventInvitationMutation__
 *
 * To run a mutation, you first call `useCancelEventInvitationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCancelEventInvitationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [cancelEventInvitationMutation, { data, loading, error }] = useCancelEventInvitationMutation({
 *   variables: {
 *      invitation: // value for 'invitation'
 *   },
 * });
 */
export function useCancelEventInvitationMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(CancelEventInvitationDocument, options);
}
export const CreateEventDocument = gql `
    mutation CreateEvent($eventInput: EventInput!) {
  response: createEvent(eventInput: $eventInput) {
    event {
      id
      archived
      publishedAt
      createdAt
      open
      dates {
        id
        startDate
        endDate
      }
      attachments {
        id
        position
        name
        downloadUrl
      }
      name
      description
      flexibleDates
      flexibleTimes
      flexibleLocation
      location {
        id
        name
        address1
        address1
        city
        zip
      }
      contentAreas
      engagementOpportunities
      gradeLevels
      connectionPreference
      status
      maximumAttendees
      inviteAllProfessionals
      inviteAllEducators
      color
      iconUrl
      iconKey
      keywords
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useCreateEventMutation__
 *
 * To run a mutation, you first call `useCreateEventMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateEventMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createEventMutation, { data, loading, error }] = useCreateEventMutation({
 *   variables: {
 *      eventInput: // value for 'eventInput'
 *   },
 * });
 */
export function useCreateEventMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(CreateEventDocument, options);
}
export const DeclineEventInvitationDocument = gql `
    mutation DeclineEventInvitation($invitation: EventInvitationInput!) {
  response: declineInvitation(invitation: $invitation) {
    invitation {
      id
      eventId
      eventName
      eventColor
      eventIconUrl
      eventDates {
        id
        startDate
        endDate
      }
      eventLocation
      profileColor
      profileDescription
      profileDescription2
      profileId
      profileName
      status
      createdAt
      notes
      updatedAt
    }
    success
    errors {
      path
      message
    }
  }
}
    `;
/**
 * __useDeclineEventInvitationMutation__
 *
 * To run a mutation, you first call `useDeclineEventInvitationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeclineEventInvitationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [declineEventInvitationMutation, { data, loading, error }] = useDeclineEventInvitationMutation({
 *   variables: {
 *      invitation: // value for 'invitation'
 *   },
 * });
 */
export function useDeclineEventInvitationMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(DeclineEventInvitationDocument, options);
}
export const DuplicateEventDocument = gql `
    mutation DuplicateEvent($eventId: ID!) {
  response: duplicateEvent(eventId: $eventId) {
    event {
      id
      archived
      publishedAt
      createdAt
      open
      dates {
        id
        startDate
        endDate
      }
      name
      description
      flexibleDates
      flexibleTimes
      flexibleLocation
      location {
        id
        name
        address1
        address1
        city
        zip
      }
      contentAreas
      engagementOpportunities
      gradeLevels
      connectionPreference
      status
      maximumAttendees
      inviteAllProfessionals
      inviteAllEducators
      color
      iconUrl
      iconKey
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useDuplicateEventMutation__
 *
 * To run a mutation, you first call `useDuplicateEventMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDuplicateEventMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [duplicateEventMutation, { data, loading, error }] = useDuplicateEventMutation({
 *   variables: {
 *      eventId: // value for 'eventId'
 *   },
 * });
 */
export function useDuplicateEventMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(DuplicateEventDocument, options);
}
export const FindEventForUserDocument = gql `
    query FindEventForUser($eventId: String!) {
  response: event(eventId: $eventId) {
    event {
      id
      archived
      publishedAt
      createdAt
      open
      dates {
        id
        startDate
        endDate
      }
      attachments {
        id
        position
        name
        downloadUrl
      }
      name
      description
      flexibleDates
      flexibleTimes
      flexibleLocation
      location {
        id
        name
        address1
        address1
        city
        zip
      }
      contentAreas
      contentAreaJobFields
      engagementOpportunities
      gradeLevels
      connectionPreference
      status
      maximumAttendees
      inviteAllProfessionals
      inviteAllEducators
      color
      iconUrl
      iconKey
      keywords
      organizedBy
      organizer {
        id
        firstName
        lastName
        avatarUrl
        color
      }
      acceptedInvitations {
        id
        profileName
        profileColor
        profileDescription
        profileDescription2
        profileIconUrl
        profileId
        status
        createdAt
      }
      pendingInvitations {
        id
        profileName
        profileColor
        profileDescription
        profileDescription2
        profileIconUrl
        profileId
        status
        createdAt
      }
      cancelledInvitations {
        id
        profileName
        profileColor
        profileDescription
        profileDescription2
        profileIconUrl
        profileId
        status
        createdAt
      }
      finishedInvitations {
        id
        profileName
        profileColor
        profileDescription
        profileDescription2
        profileIconUrl
        profileId
        status
        createdAt
      }
      reviewableInvitations {
        id
        profileName
        profileColor
        profileDescription
        profileDescription2
        profileIconUrl
        profileId
        status
        createdAt
      }
      matches {
        name
        id
        matchedProfileId
        about
        avatarUrl
        color
        title
      }
    }
    relatedRating {
      comments
      score
      id
      createdAt
    }
    relatedInvitation {
      id
      eventId
      eventName
      eventColor
      eventIconUrl
      eventDates {
        id
        startDate
        endDate
      }
      eventLocation
      profileColor
      profileDescription
      profileDescription2
      profileId
      profileName
      status
      notes
      createdAt
      updatedAt
    }
    success
    errors {
      message
      path
    }
  }
  currentUser: me {
    profile {
      ... on Profile {
        id
        role
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useFindEventForUserQuery__
 *
 * To run a query within a React component, call `useFindEventForUserQuery` and pass it any options that fit your needs.
 * When your component renders, `useFindEventForUserQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useFindEventForUserQuery({
 *   variables: {
 *      eventId: // value for 'eventId'
 *   },
 * });
 */
export function useFindEventForUserQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(FindEventForUserDocument, options);
}
export function useFindEventForUserLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(FindEventForUserDocument, options);
}
export function useFindEventForUserSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(FindEventForUserDocument, options);
}
export const FindEventsForUserDocument = gql `
    query FindEventsForUser {
  current: myEvents {
    connection {
      nodes {
        id
        archived
        publishedAt
        createdAt
        open
        dates {
          id
          startDate
          endDate
        }
        attachments {
          id
          position
          name
          downloadUrl
        }
        name
        description
        flexibleDates
        flexibleTimes
        flexibleLocation
        location {
          id
          name
          address1
          address1
          city
          zip
        }
        contentAreas
        contentAreaJobFields
        engagementOpportunities
        gradeLevels
        connectionPreference
        status
        maximumAttendees
        inviteAllProfessionals
        inviteAllEducators
        color
        iconUrl
        iconKey
      }
    }
    success
    errors {
      message
      path
    }
  }
  archived: myEvents(archived: true) {
    connection {
      nodes {
        id
        archived
        publishedAt
        createdAt
        open
        dates {
          id
          startDate
          endDate
        }
        attachments {
          id
          position
          name
          downloadUrl
        }
        name
        description
        flexibleDates
        flexibleTimes
        flexibleLocation
        location {
          id
          name
          address1
          address1
          city
          zip
        }
        contentAreas
        contentAreaJobFields
        engagementOpportunities
        gradeLevels
        connectionPreference
        status
        maximumAttendees
        inviteAllProfessionals
        inviteAllEducators
        color
        iconUrl
        iconKey
      }
    }
    success
    errors {
      message
      path
    }
  }
  pastArchived: myEvents(archived: true, past: true) {
    connection {
      nodes {
        id
        archived
        publishedAt
        createdAt
        open
        dates {
          id
          startDate
          endDate
        }
        attachments {
          id
          position
          name
          downloadUrl
        }
        name
        description
        flexibleDates
        flexibleTimes
        flexibleLocation
        location {
          id
          name
          address1
          address1
          city
          zip
        }
        contentAreas
        contentAreaJobFields
        engagementOpportunities
        gradeLevels
        connectionPreference
        status
        maximumAttendees
        inviteAllProfessionals
        inviteAllEducators
        color
        iconUrl
        iconKey
      }
    }
    success
    errors {
      message
      path
    }
  }
  currentUser: me {
    profile {
      ... on Profile {
        id
        role
        pendingInvitations {
          id
          eventDates {
            startDate
            endDate
          }
          eventName
          eventId
          eventColor
          eventIconUrl
          createdAt
          status
        }
        acceptedInvitations {
          id
          eventDates {
            startDate
            endDate
          }
          eventName
          eventId
          eventColor
          eventIconUrl
          createdAt
          status
        }
        pastInvitations {
          id
          eventDates {
            startDate
            endDate
          }
          eventName
          eventId
          eventColor
          eventIconUrl
          createdAt
          status
        }
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useFindEventsForUserQuery__
 *
 * To run a query within a React component, call `useFindEventsForUserQuery` and pass it any options that fit your needs.
 * When your component renders, `useFindEventsForUserQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useFindEventsForUserQuery({
 *   variables: {
 *   },
 * });
 */
export function useFindEventsForUserQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(FindEventsForUserDocument, options);
}
export function useFindEventsForUserLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(FindEventsForUserDocument, options);
}
export function useFindEventsForUserSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(FindEventsForUserDocument, options);
}
export const PublishEventDocument = gql `
    mutation PublishEvent($eventId: ID!) {
  response: publishEvent(eventId: $eventId) {
    event {
      id
      archived
      publishedAt
      createdAt
      open
      dates {
        id
        startDate
        endDate
      }
      name
      description
      flexibleDates
      flexibleTimes
      flexibleLocation
      location {
        id
        name
        address1
        address1
        city
        zip
      }
      contentAreas
      engagementOpportunities
      gradeLevels
      connectionPreference
      status
      maximumAttendees
      inviteAllProfessionals
      inviteAllEducators
      color
      iconUrl
      iconKey
      invitations {
        id
        profileName
        profileColor
        profileIconUrl
        createdAt
      }
      keywords
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __usePublishEventMutation__
 *
 * To run a mutation, you first call `usePublishEventMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `usePublishEventMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [publishEventMutation, { data, loading, error }] = usePublishEventMutation({
 *   variables: {
 *      eventId: // value for 'eventId'
 *   },
 * });
 */
export function usePublishEventMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(PublishEventDocument, options);
}
export const RateEventDocument = gql `
    mutation RateEvent($payload: EventRatingPayload!) {
  rateEvent(payload: $payload) {
    event {
      id
      archived
      publishedAt
      createdAt
      open
      dates {
        id
        startDate
        endDate
      }
      name
      description
      flexibleDates
      flexibleTimes
      flexibleLocation
      location {
        id
        name
        address1
        address1
        city
        zip
      }
      contentAreas
      engagementOpportunities
      gradeLevels
      connectionPreference
      status
      maximumAttendees
      inviteAllProfessionals
      inviteAllEducators
      color
      iconUrl
      iconKey
      invitations {
        id
        profileName
        profileColor
        profileIconUrl
        createdAt
      }
    }
    rating {
      id
      profileId
      profileName
      profileAvatarUrl
      comments
      score
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useRateEventMutation__
 *
 * To run a mutation, you first call `useRateEventMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useRateEventMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [rateEventMutation, { data, loading, error }] = useRateEventMutation({
 *   variables: {
 *      payload: // value for 'payload'
 *   },
 * });
 */
export function useRateEventMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(RateEventDocument, options);
}
export const ResendEventInvitationDocument = gql `
    mutation ResendEventInvitation($invitation: EventInvitationInput!) {
  response: inviteToEvent(invitation: $invitation) {
    invitation {
      id
      eventId
      eventName
      eventColor
      eventIconUrl
      eventDates {
        id
        startDate
        endDate
      }
      eventLocation
      profileColor
      profileId
      profileName
      status
      createdAt
      updatedAt
    }
    success
    errors {
      path
      message
    }
  }
}
    `;
/**
 * __useResendEventInvitationMutation__
 *
 * To run a mutation, you first call `useResendEventInvitationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useResendEventInvitationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [resendEventInvitationMutation, { data, loading, error }] = useResendEventInvitationMutation({
 *   variables: {
 *      invitation: // value for 'invitation'
 *   },
 * });
 */
export function useResendEventInvitationMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ResendEventInvitationDocument, options);
}
export const SendMessageToEventDocument = gql `
    mutation SendMessageToEvent($eventId: String!, $content: String!) {
  sendMessageToEvent(eventId: $eventId, content: $content) {
    message {
      name
      id
      threadId
      content
      createdBy
      updatedAt
    }
    thread {
      id
      messages {
        content
        createdAt
        createdBy
      }
      participants {
        color
        id
        name
        avatarUrl
      }
    }
    threadId
    success
    errors {
      path
      message
    }
  }
}
    `;
/**
 * __useSendMessageToEventMutation__
 *
 * To run a mutation, you first call `useSendMessageToEventMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useSendMessageToEventMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [sendMessageToEventMutation, { data, loading, error }] = useSendMessageToEventMutation({
 *   variables: {
 *      eventId: // value for 'eventId'
 *      content: // value for 'content'
 *   },
 * });
 */
export function useSendMessageToEventMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(SendMessageToEventDocument, options);
}
export const UpdateEventDocument = gql `
    mutation UpdateEvent($eventId: ID!, $eventInput: EventInput!) {
  response: updateEvent(eventId: $eventId, eventInput: $eventInput) {
    event {
      id
      archived
      publishedAt
      createdAt
      open
      dates {
        id
        startDate
        endDate
      }
      attachments {
        id
        name
        position
        downloadUrl
      }
      name
      description
      flexibleDates
      flexibleTimes
      flexibleLocation
      location {
        id
        name
        address1
        address1
        city
        zip
      }
      contentAreas
      contentAreaJobFields
      engagementOpportunities
      gradeLevels
      connectionPreference
      status
      keywords
      maximumAttendees
      inviteAllProfessionals
      inviteAllEducators
      color
      iconUrl
      invitations {
        id
        profileName
        profileColor
        profileIconUrl
        createdAt
      }
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useUpdateEventMutation__
 *
 * To run a mutation, you first call `useUpdateEventMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateEventMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateEventMutation, { data, loading, error }] = useUpdateEventMutation({
 *   variables: {
 *      eventId: // value for 'eventId'
 *      eventInput: // value for 'eventInput'
 *   },
 * });
 */
export function useUpdateEventMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(UpdateEventDocument, options);
}
export const CreateUploadsDocument = gql `
    mutation CreateUploads($uploadInput: UploadInput!) {
  response: createUploads(uploadInput: $uploadInput) {
    uploads {
      id
      url
      downloadUrl
    }
    success
  }
}
    `;
/**
 * __useCreateUploadsMutation__
 *
 * To run a mutation, you first call `useCreateUploadsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateUploadsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createUploadsMutation, { data, loading, error }] = useCreateUploadsMutation({
 *   variables: {
 *      uploadInput: // value for 'uploadInput'
 *   },
 * });
 */
export function useCreateUploadsMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(CreateUploadsDocument, options);
}
export const InviteableEventsForUserDocument = gql `
    query InviteableEventsForUser {
  response: myEvents(status: PUBLISHED) {
    connection {
      nodes {
        id
        publishedAt
        createdAt
        open
        dates {
          id
          startDate
          endDate
        }
        name
        description
        flexibleDates
        flexibleLocation
        location {
          id
          name
          address1
          address1
          city
          zip
        }
        contentAreas
        engagementOpportunities
        gradeLevels
        connectionPreference
        status
        maximumAttendees
        inviteAllProfessionals
        inviteAllEducators
        color
        iconUrl
        iconKey
        organizedBy
      }
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useInviteableEventsForUserQuery__
 *
 * To run a query within a React component, call `useInviteableEventsForUserQuery` and pass it any options that fit your needs.
 * When your component renders, `useInviteableEventsForUserQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useInviteableEventsForUserQuery({
 *   variables: {
 *   },
 * });
 */
export function useInviteableEventsForUserQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(InviteableEventsForUserDocument, options);
}
export function useInviteableEventsForUserLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(InviteableEventsForUserDocument, options);
}
export function useInviteableEventsForUserSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(InviteableEventsForUserDocument, options);
}
export const InviteMatchToEventDocument = gql `
    mutation InviteMatchToEvent($invitation: EventInvitationInput!) {
  response: inviteToEvent(invitation: $invitation) {
    invitation {
      id
      eventId
      eventName
      eventColor
      eventIconUrl
      eventDates {
        id
        startDate
        endDate
      }
      eventLocation
      profileColor
      profileId
      profileName
      status
      createdAt
      updatedAt
    }
    success
    errors {
      path
      message
    }
  }
}
    `;
/**
 * __useInviteMatchToEventMutation__
 *
 * To run a mutation, you first call `useInviteMatchToEventMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useInviteMatchToEventMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [inviteMatchToEventMutation, { data, loading, error }] = useInviteMatchToEventMutation({
 *   variables: {
 *      invitation: // value for 'invitation'
 *   },
 * });
 */
export function useInviteMatchToEventMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(InviteMatchToEventDocument, options);
}
export const MatchListDataDocument = gql `
    query MatchListData($priority: ProfileMatchPriority, $filter: ProfileMatchFilter) {
  response: me {
    profile {
      ... on Profile {
        id
        matches(priority: $priority, filter: $filter) {
          id
          createdAt
          color
          about
          matchedProfileId
          avatarUrl
          hasUnreadMessages
          name
          title
          score
          favorite
          blocked
          notes
          threadId
        }
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useMatchListDataQuery__
 *
 * To run a query within a React component, call `useMatchListDataQuery` and pass it any options that fit your needs.
 * When your component renders, `useMatchListDataQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useMatchListDataQuery({
 *   variables: {
 *      priority: // value for 'priority'
 *      filter: // value for 'filter'
 *   },
 * });
 */
export function useMatchListDataQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(MatchListDataDocument, options);
}
export function useMatchListDataLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(MatchListDataDocument, options);
}
export function useMatchListDataSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(MatchListDataDocument, options);
}
export const MatchProfileDetailDocument = gql `
    query MatchProfileDetail($id: String!) {
  response: profile(id: $id) {
    profile {
      ... on Profile {
        id
        bio
        avatarUrl
        color
        role
        firstName
        lastName
        about
        unavailableAt {
          id
          startDate
          endDate
        }
        unavailable
        unavailableUntil
        match {
          id
          matchStrengths {
            id
            label
            value
            score
          }
          threadId
          hasUnreadMessages
          favorite
          blocked
          notes
        }
      }
      ... on Educator {
        districtName
        schoolName
      }
      ... on Professional {
        companyName
        jobField
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useMatchProfileDetailQuery__
 *
 * To run a query within a React component, call `useMatchProfileDetailQuery` and pass it any options that fit your needs.
 * When your component renders, `useMatchProfileDetailQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useMatchProfileDetailQuery({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useMatchProfileDetailQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(MatchProfileDetailDocument, options);
}
export function useMatchProfileDetailLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(MatchProfileDetailDocument, options);
}
export function useMatchProfileDetailSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(MatchProfileDetailDocument, options);
}
export const SendMessageToMatchDocument = gql `
    mutation SendMessageToMatch($profileId: String!, $content: String!) {
  sendMessageToUser(profileId: $profileId, content: $content) {
    message {
      name
      id
      threadId
      content
      createdBy
      updatedAt
    }
    match {
      id
      createdAt
      color
      about
      matchedProfileId
      avatarUrl
      name
      title
      score
      favorite
      blocked
      hasUnreadMessages
      notes
      threadId
    }
    thread {
      id
      messages {
        content
        createdAt
        createdBy
      }
      participants {
        color
        id
        name
        avatarUrl
      }
    }
    threadId
    success
    errors {
      path
      message
    }
  }
}
    `;
/**
 * __useSendMessageToMatchMutation__
 *
 * To run a mutation, you first call `useSendMessageToMatchMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useSendMessageToMatchMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [sendMessageToMatchMutation, { data, loading, error }] = useSendMessageToMatchMutation({
 *   variables: {
 *      profileId: // value for 'profileId'
 *      content: // value for 'content'
 *   },
 * });
 */
export function useSendMessageToMatchMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(SendMessageToMatchDocument, options);
}
export const SetProfileMatchMetaDataDocument = gql `
    mutation SetProfileMatchMetaData($id: String!, $preference: ProfileMatchPreference, $notes: String) {
  response: setProfileMatchMetaData(
    id: $id
    preference: $preference
    notes: $notes
  ) {
    profile {
      ... on Profile {
        id
        matches {
          id
          createdAt
          color
          about
          matchedProfileId
          avatarUrl
          name
          title
          score
          hasUnreadMessages
          favorite
          blocked
          threadId
          notes
        }
      }
      ... on Educator {
        districtName
        schoolName
      }
      ... on Professional {
        companyName
        jobField
      }
    }
  }
}
    `;
/**
 * __useSetProfileMatchMetaDataMutation__
 *
 * To run a mutation, you first call `useSetProfileMatchMetaDataMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useSetProfileMatchMetaDataMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [setProfileMatchMetaDataMutation, { data, loading, error }] = useSetProfileMatchMetaDataMutation({
 *   variables: {
 *      id: // value for 'id'
 *      preference: // value for 'preference'
 *      notes: // value for 'notes'
 *   },
 * });
 */
export function useSetProfileMatchMetaDataMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(SetProfileMatchMetaDataDocument, options);
}
export const ArchiveThreadDocument = gql `
    mutation ArchiveThread($threadId: String!) {
  archiveThread(threadId: $threadId) {
    message {
      name
      id
      threadId
      content
      createdBy
      updatedAt
      archived
      readByUser
    }
    match {
      id
      createdAt
      color
      about
      matchedProfileId
      avatarUrl
      name
      title
      score
      favorite
      blocked
      hasUnreadMessages
      notes
      threadId
    }
    success
    errors {
      path
      message
    }
  }
}
    `;
/**
 * __useArchiveThreadMutation__
 *
 * To run a mutation, you first call `useArchiveThreadMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useArchiveThreadMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [archiveThreadMutation, { data, loading, error }] = useArchiveThreadMutation({
 *   variables: {
 *      threadId: // value for 'threadId'
 *   },
 * });
 */
export function useArchiveThreadMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ArchiveThreadDocument, options);
}
export const MessagesDocument = gql `
    query Messages {
  unread: messages(status: UNREAD) {
    messages {
      id
      eventId
      eventIconUrl
      eventName
      participantCount
      content
      avatarUrl
      color
      name
      profileId
      threadId
      updatedAt
      readByUser
      archived
    }
    success
    errors {
      path
      message
    }
  }
  read: messages(status: READ) {
    messages {
      id
      eventId
      eventIconUrl
      eventName
      participantCount
      content
      avatarUrl
      color
      name
      profileId
      threadId
      updatedAt
      readByUser
      archived
    }
    success
    errors {
      path
      message
    }
  }
  sent: messages(status: SENT) {
    messages {
      id
      eventId
      eventIconUrl
      eventName
      participantCount
      content
      avatarUrl
      color
      name
      profileId
      threadId
      updatedAt
      readByUser
      archived
    }
    success
    errors {
      path
      message
    }
  }
}
    `;
/**
 * __useMessagesQuery__
 *
 * To run a query within a React component, call `useMessagesQuery` and pass it any options that fit your needs.
 * When your component renders, `useMessagesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useMessagesQuery({
 *   variables: {
 *   },
 * });
 */
export function useMessagesQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(MessagesDocument, options);
}
export function useMessagesLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(MessagesDocument, options);
}
export function useMessagesSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(MessagesDocument, options);
}
export const ReadThreadDocument = gql `
    mutation ReadThread($threadId: String!) {
  readThread(threadId: $threadId) {
    message {
      id
      name
      threadId
      content
      createdBy
      updatedAt
      archived
      readByUser
    }
    match {
      id
      createdAt
      color
      about
      matchedProfileId
      avatarUrl
      name
      title
      score
      favorite
      blocked
      hasUnreadMessages
      notes
      threadId
    }
    success
    errors {
      path
      message
    }
  }
}
    `;
/**
 * __useReadThreadMutation__
 *
 * To run a mutation, you first call `useReadThreadMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useReadThreadMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [readThreadMutation, { data, loading, error }] = useReadThreadMutation({
 *   variables: {
 *      threadId: // value for 'threadId'
 *   },
 * });
 */
export function useReadThreadMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ReadThreadDocument, options);
}
export const ReplyToThreadDocument = gql `
    mutation ReplyToThread($threadId: String!, $content: String!) {
  sendMessageToThread(threadId: $threadId, content: $content) {
    message {
      name
      id
      threadId
      content
      createdBy
      updatedAt
      archived
      readByUser
    }
    thread {
      id
      messages {
        content
        createdAt
        createdBy
      }
      participants {
        color
        id
        name
        avatarUrl
      }
    }
    threadId
    success
    errors {
      path
      message
    }
  }
}
    `;
/**
 * __useReplyToThreadMutation__
 *
 * To run a mutation, you first call `useReplyToThreadMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useReplyToThreadMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [replyToThreadMutation, { data, loading, error }] = useReplyToThreadMutation({
 *   variables: {
 *      threadId: // value for 'threadId'
 *      content: // value for 'content'
 *   },
 * });
 */
export function useReplyToThreadMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ReplyToThreadDocument, options);
}
export const ThreadDocument = gql `
    query Thread($threadId: String!) {
  response: thread(threadId: $threadId) {
    currentUserProfileId
    thread {
      id
      messages {
        content
        createdAt
        createdBy
      }
      participants {
        color
        id
        name
        avatarUrl
      }
      unavailableUntil
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useThreadQuery__
 *
 * To run a query within a React component, call `useThreadQuery` and pass it any options that fit your needs.
 * When your component renders, `useThreadQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useThreadQuery({
 *   variables: {
 *      threadId: // value for 'threadId'
 *   },
 * });
 */
export function useThreadQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(ThreadDocument, options);
}
export function useThreadLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(ThreadDocument, options);
}
export function useThreadSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(ThreadDocument, options);
}
export const ResourceDashboardDocument = gql `
    query ResourceDashboard {
  featured: search(queryString: "", published: true) {
    documents(page: 0, pageSize: 2, facetFilters: ["featured:true"]) {
      nodes {
        id
        slug
        title
        color
        iconUrl
        contentPreview
        categoryId
        categorySlug
        categoryName
        iconUrl
        category {
          id
          name
          slug
        }
        excerpt
        featured
        status
      }
      searchInfo {
        page
        pageCount
        facets
      }
    }
    success
  }
  latest: search(queryString: "", published: true) {
    documents(page: 0, pageSize: 4) {
      nodes {
        id
        slug
        title
        color
        iconUrl
        contentPreview
        categoryId
        categorySlug
        categoryName
        iconUrl
        category {
          id
          name
          slug
        }
        excerpt
        featured
        status
      }
      searchInfo {
        page
        pageCount
        facets
      }
    }
    success
  }
}
    `;
/**
 * __useResourceDashboardQuery__
 *
 * To run a query within a React component, call `useResourceDashboardQuery` and pass it any options that fit your needs.
 * When your component renders, `useResourceDashboardQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useResourceDashboardQuery({
 *   variables: {
 *   },
 * });
 */
export function useResourceDashboardQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(ResourceDashboardDocument, options);
}
export function useResourceDashboardLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(ResourceDashboardDocument, options);
}
export function useResourceDashboardSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(ResourceDashboardDocument, options);
}
export const ResourceDocumentsSearchDocument = gql `
    query ResourceDocumentsSearch($queryString: String!, $facetFilters: [String], $page: Int, $pageSize: Int) {
  search(queryString: $queryString, published: true) {
    documents(page: $page, pageSize: $pageSize, facetFilters: $facetFilters) {
      nodes {
        id
        slug
        title
        color
        iconUrl
        contentPreview
        categoryId
        categoryName
        categorySlug
        iconUrl
        category {
          id
          name
          slug
        }
        excerpt
        featured
        status
      }
      searchInfo {
        page
        pageCount
        facets
      }
    }
    success
  }
}
    `;
/**
 * __useResourceDocumentsSearchQuery__
 *
 * To run a query within a React component, call `useResourceDocumentsSearchQuery` and pass it any options that fit your needs.
 * When your component renders, `useResourceDocumentsSearchQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useResourceDocumentsSearchQuery({
 *   variables: {
 *      queryString: // value for 'queryString'
 *      facetFilters: // value for 'facetFilters'
 *      page: // value for 'page'
 *      pageSize: // value for 'pageSize'
 *   },
 * });
 */
export function useResourceDocumentsSearchQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(ResourceDocumentsSearchDocument, options);
}
export function useResourceDocumentsSearchLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(ResourceDocumentsSearchDocument, options);
}
export function useResourceDocumentsSearchSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(ResourceDocumentsSearchDocument, options);
}
export const CreateSavedSearchDocument = gql `
    mutation CreateSavedSearch($payload: SavedSearchPayload!) {
  createSearch(payload: $payload) {
    search {
      id
      name
      facetFilters
      queryString
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useCreateSavedSearchMutation__
 *
 * To run a mutation, you first call `useCreateSavedSearchMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateSavedSearchMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createSavedSearchMutation, { data, loading, error }] = useCreateSavedSearchMutation({
 *   variables: {
 *      payload: // value for 'payload'
 *   },
 * });
 */
export function useCreateSavedSearchMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(CreateSavedSearchDocument, options);
}
export const DestroySavedSearchDocument = gql `
    mutation DestroySavedSearch($id: ID!) {
  destroySearch(id: $id) {
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useDestroySavedSearchMutation__
 *
 * To run a mutation, you first call `useDestroySavedSearchMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDestroySavedSearchMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [destroySavedSearchMutation, { data, loading, error }] = useDestroySavedSearchMutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useDestroySavedSearchMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(DestroySavedSearchDocument, options);
}
export const SavedSearchesDocument = gql `
    query SavedSearches {
  savedSearches {
    searches {
      id
      name
      queryString
      facetFilters
    }
    success
    errors {
      path
      message
    }
  }
  me {
    profile {
      ... on Profile {
        id
        matches(filter: FAVORITES) {
          id
          name
        }
      }
    }
    errors {
      message
      path
    }
    success
  }
}
    `;
/**
 * __useSavedSearchesQuery__
 *
 * To run a query within a React component, call `useSavedSearchesQuery` and pass it any options that fit your needs.
 * When your component renders, `useSavedSearchesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useSavedSearchesQuery({
 *   variables: {
 *   },
 * });
 */
export function useSavedSearchesQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(SavedSearchesDocument, options);
}
export function useSavedSearchesLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(SavedSearchesDocument, options);
}
export function useSavedSearchesSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(SavedSearchesDocument, options);
}
export const ChangePasswordDocument = gql `
    mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
  response: changePassword(
    currentPassword: $currentPassword
    newPassword: $newPassword
  ) {
    user {
      emailAddress
    }
    errors {
      path
      message
    }
    success
  }
}
    `;
/**
 * __useChangePasswordMutation__
 *
 * To run a mutation, you first call `useChangePasswordMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useChangePasswordMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [changePasswordMutation, { data, loading, error }] = useChangePasswordMutation({
 *   variables: {
 *      currentPassword: // value for 'currentPassword'
 *      newPassword: // value for 'newPassword'
 *   },
 * });
 */
export function useChangePasswordMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ChangePasswordDocument, options);
}
export const ChangeProfileActivationDocument = gql `
    mutation ChangeProfileActivation($active: Boolean!) {
  response: updateCurrentProfileActivation(active: $active) {
    profile {
      ... on Profile {
        avatarUrl
        id
        color
        firstName
        lastName
        status
        unavailableAt {
          id
          startDate
          endDate
        }
      }
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useChangeProfileActivationMutation__
 *
 * To run a mutation, you first call `useChangeProfileActivationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useChangeProfileActivationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [changeProfileActivationMutation, { data, loading, error }] = useChangeProfileActivationMutation({
 *   variables: {
 *      active: // value for 'active'
 *   },
 * });
 */
export function useChangeProfileActivationMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ChangeProfileActivationDocument, options);
}
export const ChangeProfileAvailabilityDocument = gql `
    mutation ChangeProfileAvailability($unavailableAt: [ProfileDateRangeInput]!) {
  response: updateCurrentProfileAvailability(unavailableAt: $unavailableAt) {
    profile {
      ... on Profile {
        avatarUrl
        id
        color
        firstName
        lastName
        status
        unavailableAt {
          id
          startDate
          endDate
        }
      }
    }
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useChangeProfileAvailabilityMutation__
 *
 * To run a mutation, you first call `useChangeProfileAvailabilityMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useChangeProfileAvailabilityMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [changeProfileAvailabilityMutation, { data, loading, error }] = useChangeProfileAvailabilityMutation({
 *   variables: {
 *      unavailableAt: // value for 'unavailableAt'
 *   },
 * });
 */
export function useChangeProfileAvailabilityMutation(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useMutation(ChangeProfileAvailabilityDocument, options);
}
export const UserProfileForSettingsDocument = gql `
    query UserProfileForSettings {
  response: me {
    profile {
      ... on Profile {
        avatarUrl
        id
        color
        firstName
        lastName
        role
        status
        unavailableAt {
          id
          startDate
          endDate
        }
      }
    }
    success
    errors {
      message
      path
    }
  }
}
    `;
/**
 * __useUserProfileForSettingsQuery__
 *
 * To run a query within a React component, call `useUserProfileForSettingsQuery` and pass it any options that fit your needs.
 * When your component renders, `useUserProfileForSettingsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useUserProfileForSettingsQuery({
 *   variables: {
 *   },
 * });
 */
export function useUserProfileForSettingsQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(UserProfileForSettingsDocument, options);
}
export function useUserProfileForSettingsLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(UserProfileForSettingsDocument, options);
}
export function useUserProfileForSettingsSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(UserProfileForSettingsDocument, options);
}
export const ContentAreasDocument = gql `
    query ContentAreas($lowerLevel: String, $coreIdea: String, $standard: String) {
  response: contentAreas(
    lowerLevel: $lowerLevel
    coreIdea: $coreIdea
    standard: $standard
  ) {
    contentAreas {
      id
      standard
      coreIdea
      lowerLevel
    }
    success
    pageSize
  }
}
    `;
/**
 * __useContentAreasQuery__
 *
 * To run a query within a React component, call `useContentAreasQuery` and pass it any options that fit your needs.
 * When your component renders, `useContentAreasQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useContentAreasQuery({
 *   variables: {
 *      lowerLevel: // value for 'lowerLevel'
 *      coreIdea: // value for 'coreIdea'
 *      standard: // value for 'standard'
 *   },
 * });
 */
export function useContentAreasQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(ContentAreasDocument, options);
}
export function useContentAreasLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(ContentAreasDocument, options);
}
export function useContentAreasSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(ContentAreasDocument, options);
}
export const JobFieldsDocument = gql `
    query JobFields($jobField: String) {
  response: jobFields(jobField: $jobField) {
    jobFields {
      id
      standard
      field
      lowerLevel
    }
    success
    pageSize
  }
}
    `;
/**
 * __useJobFieldsQuery__
 *
 * To run a query within a React component, call `useJobFieldsQuery` and pass it any options that fit your needs.
 * When your component renders, `useJobFieldsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useJobFieldsQuery({
 *   variables: {
 *      jobField: // value for 'jobField'
 *   },
 * });
 */
export function useJobFieldsQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useQuery(JobFieldsDocument, options);
}
export function useJobFieldsLazyQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useLazyQuery(JobFieldsDocument, options);
}
export function useJobFieldsSuspenseQuery(baseOptions) {
    const options = Object.assign(Object.assign({}, defaultOptions), baseOptions);
    return Apollo.useSuspenseQuery(JobFieldsDocument, options);
}
const result = {
    "possibleTypes": {
        "Profile": [
            "Educator",
            "Professional"
        ],
        "UserProfile": [
            "Educator",
            "Professional"
        ]
    }
};
export default result;
